import React from "react";
import { useNavigate } from "react-router-dom";
import { AsciiButton, AsciiPanel } from "@/components/ui/ascii-theme";

const Homepage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white text-black px-2 sm:px-4 py-2 sm:py-4 font-mono text-xs sm:text-sm">
      <div className="w-full max-w-4xl mx-auto">
        {/* ASCII Art Header */}
        <pre className="hidden sm:block text-xs mb-6 select-none">
          {`┌──────────────────────────────────────────────────────────────────┐
│                                                                  │
│   ███╗   ███╗ █████╗ ██╗  ██╗    ███╗   ███╗ █████╗ ██╗ ██████╗  │
│   ████╗ ████║██╔══██╗╚██╗██╔╝    ████╗ ████║██╔══██╗██║██╔═══██╗ │
│   ██╔████╔██║███████║ ╚███╔╝     ██╔████╔██║███████║██║██║   ██║ │
│   ██║╚██╔╝██║██╔══██║ ██╔██╗     ██║╚██╔╝██║██╔══██║██║██║   ██║ │
│   ██║ ╚═╝ ██║██║  ██║██╔╝ ██╗    ██║ ╚═╝ ██║██║  ██║██║╚██████╔╝ │
│   ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝    ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝ ╚═════╝  │
│                                                                  │
└──────────────────────────────────────────────────────────────────┘`}
        </pre>

        {/* Mobile Header */}
        <pre className="sm:hidden text-[0.6rem] mb-4 select-none leading-tight">
          {`┌──────────────────────────────────┐
│                                  │
│  ███╗   ███╗ █████╗ ██╗  ██╗     │
│  ████╗ ████║██╔══██╗╚██╗██╔╝     │
│  ██╔████╔██║███████║ ╚███╔╝      │
│  ██║╚██╔╝██║██╔══██║ ██╔██╗      │
│  ██║ ╚═╝ ██║██║  ██║██╔╝ ██╗     │
│  ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝  ╚═╝     │
│                                  │
│  ███╗   ███╗ █████╗ ██╗ ██████╗  │
│  ████╗ ████║██╔══██╗██║██╔═══██╗ │
│  ██╔████╔██║███████║██║██║   ██║ │
│  ██║╚██╔╝██║██╔══██║██║██║   ██║ │
│  ██║ ╚═╝ ██║██║  ██║██║╚██████╔╝ │
│  ╚═╝     ╚═╝╚═╝  ╚═╝╚═╝ ╚═════╝  │
│                                  │
└──────────────────────────────────┘`}
        </pre>

        {/* Welcome Panel */}
        <AsciiPanel title="═══ WELCOME ═══" className="mb-4 sm:mb-6">
          <div className="space-y-3 sm:space-y-4 text-xs sm:text-sm">
            <p className="leading-relaxed">Hi, I'm Max Maio.</p>
            <p className="leading-relaxed">
              I'm one of the founders of{" "}
              <a
                className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                href="https://midship.ai"
                target="_blank"
                rel="noopener noreferrer"
              >
                Midship AI
              </a>
              .
            </p>
            <p className="leading-relaxed">
              You can reach me at{" "}
              <a
                className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                href="mailto:max@midship.ai"
              >
                max@midship.ai
              </a>
            </p>
          </div>
        </AsciiPanel>

        <AsciiPanel title="═══ ABOUT ═══" className="mb-4 sm:mb-6">
          <div className="space-y-3 text-xs">
            <div>
              <p className="uppercase opacity-60 font-bold mb-2">└─ About</p>
              <p className="pl-4 leading-relaxed">
                Building{" "}
                <a
                  className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                  href="https://midship.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Midship
                </a>
                . Previously engineering at various startups (
                <a
                  className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                  href="https://dashworks.ai"
                  target="_blank"
                >
                  Dashworks - acquired by Hubspot
                </a>
                ,{" "}
                <a
                  className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                  href="https://lumanu.com"
                  target="_blank"
                >
                  Lumanu
                </a>
                ) and companies (
                <a
                  className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                  href="https://paypal.com"
                  target="_blank"
                >
                  PayPal
                </a>
                ).
              </p>
            </div>
            <div>
              <p className="uppercase opacity-60 font-bold mb-2">
                └─ This Site
              </p>
              <div className="pl-4 space-y-1 leading-relaxed">
                <p>
                  <span className="opacity-60">[code] </span>
                  <a
                    className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                    href="https://github.com/maxmaio"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Github
                  </a>
                </p>
                <p>
                  <span className="opacity-60">[profile] </span>
                  <a
                    className="underline font-bold hover:bg-[#fbf1c7] transition-colors px-1"
                    href="https://www.linkedin.com/in/max-maio-897514195/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    LinkedIn
                  </a>
                </p>
              </div>
            </div>
          </div>
        </AsciiPanel>
        {/* Tools Panel */}
        <AsciiPanel title="═══ PERSONAL TOOLS ═══" className="mb-4 sm:mb-6">
          <div className="space-y-3">
            <p className="text-xs uppercase opacity-60 font-bold mb-3">
              └─ Tools
            </p>

            {/* Weather Tool */}
            <div className="border-2 border-black p-3 hover:bg-[#fbf1c7] transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <h3 className="text-sm sm:text-base font-bold uppercase">
                    ASCII Weather
                  </h3>
                  <div className="flex flex-wrap gap-1 text-[0.65rem] opacity-60">
                    <span className="border border-black px-1">REALTIME</span>
                    <span className="border border-black px-1">
                      HOURLY
                    </span>
                    <span className="border border-black px-1">10-DAY</span>
                  </div>
                </div>
                <AsciiButton
                  variant="default"
                  size="md"
                  onClick={() => navigate("/weather")}
                >
                  OPEN WEATHER
                </AsciiButton>
              </div>
            </div>

            {/* Notes Tool */}
            <div className="border-2 border-black p-3 hover:bg-[#fbf1c7] transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <h3 className="text-sm sm:text-base font-bold uppercase">
                    Plaintext Notes
                  </h3>
                  <div className="flex flex-wrap gap-1 text-[0.65rem] opacity-60">
                    <span className="border border-black px-1">REALTIME</span>
                    <span className="border border-black px-1">
                      COLLABORATIVE
                    </span>
                    <span className="border border-black px-1">SHAREABLE</span>
                  </div>
                </div>
                <AsciiButton
                  variant="default"
                  size="md"
                  onClick={() => navigate("/notes")}
                >
                  OPEN NOTES
                </AsciiButton>
              </div>
            </div>

            {/* Screen Recorder Tool */}
            <div className="border-2 border-black p-3 hover:bg-[#fbf1c7] transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <h3 className="text-sm sm:text-base font-bold uppercase">
                    Screen Recorder
                  </h3>
                  <div className="flex flex-wrap gap-1 text-[0.65rem] opacity-60">
                    <span className="border border-black px-1">MP4/WEBM</span>
                    <span className="border border-black px-1">LIBRARY</span>
                    <span className="border border-black px-1">DOWNLOAD</span>
                  </div>
                </div>
                <AsciiButton
                  variant="default"
                  size="md"
                  onClick={() => navigate("/asset_recorder")}
                >
                  OPEN RECORDER
                </AsciiButton>
              </div>
            </div>

            {/* Video Library */}
            <div className="border-2 border-black p-3 hover:bg-[#fbf1c7] transition-colors">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="space-y-1">
                  <h3 className="text-sm sm:text-base font-bold uppercase">
                    Video Library
                  </h3>
                  <div className="flex flex-wrap gap-1 text-[0.65rem] opacity-60">
                    <span className="border border-black px-1">ORGANIZE</span>
                    <span className="border border-black px-1">STREAM</span>
                    <span className="border border-black px-1">SHARE</span>
                  </div>
                </div>
                <AsciiButton
                  variant="default"
                  size="md"
                  onClick={() => navigate("/asset_library")}
                >
                  OPEN LIBRARY
                </AsciiButton>
              </div>
            </div>

            {/* Login Prompt */}
          </div>
        </AsciiPanel>

        {/* About Panel */}

        {/* Footer */}
        <div className="mt-8 pt-4 border-t-2 border-black">
          <pre className="text-center text-[0.65rem] sm:text-xs opacity-60">
            {`─────────────────────────────────────────────────────────────────
© ${new Date().getFullYear()} Max Maio | San Francisco, CA | midship.ai
─────────────────────────────────────────────────────────────────`}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default Homepage;
